// Host audio output (plan §8). The sandbox drains rtw_audio_* each frame batch and sends {rate, samples}; we play
// them through an AudioWorklet (ScriptProcessor where AudioWorklet is missing) with the rate-controlled queue.
// Starts on the first user gesture. Mute and volume are kept per browser. Audio never paces the game: before it
// starts (or if it fails) samples are dropped.
import { AudioQueue, int16ToFloat, type QueueStats } from './queue.ts';
import workletUrl from './worklet.ts?worker&url';
import { loadPref, savePref } from '../lib/prefs.ts';

export interface AudioPrefs { muted: boolean; volume: number; fallback?: boolean }
const PREF = 'rtw:audio';

export interface AudioState extends Partial<QueueStats> { state: string; kind: string; deviceRate: number; muted: boolean; volume: number }

export class AudioOut {
  prefs: AudioPrefs;
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private post: ((rate: number, s: Float32Array) => void) | null = null;
  private holdOut: ((on: boolean) => void) | null = null;
  /** True from pause(true) until the device runs again after pause(false): samples are kept, the output holds. */
  private holding = false;
  private pauseSeq = 0;
  private starting = false;
  private kind = '';
  private stats: QueueStats | null = null;
  private stopped = false;
  onChange?: () => void;

  constructor() {
    const p = loadPref<Partial<AudioPrefs>>(PREF, {});
    this.prefs = { muted: !!p.muted, volume: typeof p.volume === 'number' ? Math.max(0, Math.min(1, p.volume)) : 0.8, fallback: !!p.fallback };
  }

  /** Call from a user gesture (or one the sandbox relayed). Safe to call repeatedly. */
  async start(): Promise<void> {
    if (this.stopped) return;
    if (this.ctx) { if (this.ctx.state !== 'running' && !this.holding) await this.ctx.resume().catch(() => {}); this.onChange?.(); return; }
    if (this.starting) return;
    this.starting = true;
    try {
      const AC = globalThis.AudioContext ?? (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) { this.kind = 'unsupported'; return; }
      const ctx = new AC({ latencyHint: 'interactive' });
      this.ctx = ctx;
      this.gain = ctx.createGain();
      this.gain.connect(ctx.destination);
      this.apply();
      let node: AudioNode;
      if (ctx.audioWorklet && typeof AudioWorkletNode !== 'undefined' && !this.prefs.fallback) {
        await ctx.audioWorklet.addModule(workletUrl);
        const w = new AudioWorkletNode(ctx, 'rtw-audio', { numberOfInputs: 0, outputChannelCount: [2] });
        w.port.onmessage = (e: MessageEvent<QueueStats>) => { this.stats = e.data; this.onChange?.(); };
        this.post = (rate, samples) => w.port.postMessage({ rate, samples }, [samples.buffer]);
        this.holdOut = (on) => w.port.postMessage({ hold: on });
        node = w;
        this.kind = 'worklet';
      } else {
        let q: AudioQueue | null = null;
        let held = false;
        const sp = ctx.createScriptProcessor(1024, 0, 2);
        let n = 0;
        sp.onaudioprocess = (e) => {
          const L = e.outputBuffer.getChannelData(0), R = e.outputBuffer.getChannelData(1);
          if (!q) { L.fill(0); R.fill(0); return; }
          for (let o = 0; o < L.length; o += 128) q.fill(L.subarray(o, o + 128), R.subarray(o, o + 128));
          if (++n % 12 === 0) { this.stats = q.stats(); this.onChange?.(); }
        };
        this.post = (rate, samples) => {
          if (!q || q.srcRate !== rate) { q = new AudioQueue(rate, ctx.sampleRate); if (held) q.hold(true); }
          q.push(samples);
        };
        this.holdOut = (on) => { held = on; q?.hold(on); };
        node = sp;
        this.kind = 'scriptprocessor';
      }
      node.connect(this.gain);
      if (ctx.state !== 'running') await ctx.resume().catch(() => {});
    } catch (e) {
      this.kind = 'error: ' + String((e as Error)?.message ?? e);
      this.post = null;
    } finally {
      this.starting = false;
      this.onChange?.();
    }
  }

  /** int16 interleaved stereo at `rate` Hz from rtw_audio_buffer. */
  feed(rate: number, samples: ArrayBuffer): void {
    // While we pause and resume, the context is suspended or still waking up: keep those samples (the output holds,
    // see pause), or the queue would come back short and run dry.
    if (!this.post || !this.ctx || (this.ctx.state !== 'running' && !this.holding) || rate < 8000 || rate > 192000) return;
    this.post(rate, int16ToFloat(samples));
  }

  setMuted(m: boolean) { this.prefs.muted = m; this.save(); }
  setVolume(v: number) { this.prefs.volume = Math.max(0, Math.min(1, v)); this.save(); }
  private save() { savePref(PREF, this.prefs); this.apply(); this.onChange?.(); }
  private apply() { if (this.gain) this.gain.gain.value = this.prefs.muted ? 0 : this.prefs.volume; }

  /**
   * Suspend while the game is paused. suspend() and resume() take effect some time after the call (tens of ms on
   * real devices), while the game stops and restarts at once: without help the output kept playing the queue after
   * the game stopped and dropped the game's first samples until the device ran again, so the queue came back short,
   * ran dry and re-buffered (a ~0.1 s gap after every pause), or, where the device woke faster, came back longer.
   * So the queue is held (silence, nothing consumed) from the pause until the device runs again, every sample in
   * between is kept, and releasing the hold trims the queue back to its target.
   */
  pause(on: boolean) {
    if (!this.ctx) return;
    const seq = ++this.pauseSeq;
    if (on) {
      this.holding = true;
      this.holdOut?.(true);
      this.ctx.suspend().catch(() => {});
    } else {
      const ctx = this.ctx;
      const release = () => { if (seq === this.pauseSeq && this.ctx === ctx) { this.holding = false; this.holdOut?.(false); } };
      ctx.resume().then(release, release);
    }
  }

  state(): AudioState {
    return { state: this.ctx?.state ?? 'off', kind: this.kind, deviceRate: this.ctx?.sampleRate ?? 0, muted: this.prefs.muted,
      volume: this.prefs.volume, ...(this.stats ?? {}) };
  }

  close() { this.stopped = true; this.post = null; this.holdOut = null; this.ctx?.close().catch(() => {}); this.ctx = null; }
}
